import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'

// Map-pin pictures are shared with the mobile app. The pin document's `images`
// field stores a bare id per picture (a UUID, no path, no extension); the bytes
// live in Cloud Storage in the DEFAULT bucket under a single flat path,
// `mapPinsImages/{id}.png`, with contentType `image/png` and no resizing. This
// family (`mapPinsImages/…`) is shared with mobile's game-pins and lines, so the
// path and content type must match mobile's writes exactly — a web-written
// picture has to be readable by the app and vice-versa.
const MAP_PIN_IMAGES_PREFIX = 'mapPinsImages'
const MAP_PIN_IMAGE_CONTENT_TYPE = 'image/png'

function objectPath(id: string): string {
  return `${MAP_PIN_IMAGES_PREFIX}/${id}.png`
}

/**
 * Uploads a picture's bytes under a freshly-minted id and returns that id (the
 * value the caller stores in the pin's `images` list). The bytes are written
 * verbatim — no resizing, content type pinned to `image/png` to match mobile.
 */
export async function uploadMapPinImage(bytes: Blob): Promise<string> {
  const id = crypto.randomUUID()
  const storage = getStorage()
  await uploadBytes(ref(storage, objectPath(id)), bytes, {
    contentType: MAP_PIN_IMAGE_CONTENT_TYPE,
  })
  return id
}

/** Resolves a stored picture id to a display download URL. */
export async function mapPinImageUrl(id: string): Promise<string> {
  const storage = getStorage()
  return getDownloadURL(ref(storage, objectPath(id)))
}

/**
 * Deletes a picture's Storage object. A missing object is not an error: the
 * pin's `images` list is the source of truth, and a delete that races another
 * client (or retries after a partial success) must be idempotent.
 */
export async function deleteMapPinImage(id: string): Promise<void> {
  const storage = getStorage()
  try {
    await deleteObject(ref(storage, objectPath(id)))
  } catch (e) {
    if ((e as { code?: string })?.code === 'storage/object-not-found') return
    throw e
  }
}
